import React, { useRef, useEffect, useState } from 'react'
import { useMusicPlayer } from './MusicPlayerContext.jsx'

export function HiddenAudioController() {
  const audioRef = useRef()
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  
  const { 
    currentTrack, 
    volume,
    GLOBAL_AUDIO_SCALE,
    registerAudio, 
    setCurrentTime, 
    setDuration,
    setIsPlaying,
    nextTrack
  } = useMusicPlayer()

  // Register HTML audio ref with the music player context
  useEffect(() => {
    if (audioRef.current) {
      registerAudio(audioRef.current)
    }
  }, [registerAudio])

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      
      // Debug logging for dirt track
      if (currentTrack?.id === 'my_track_1' && audio.currentTime > 160 && audio.currentTime < 170) {
        console.log(`🎵 Dirt Track Debug at ${audio.currentTime.toFixed(1)}s:`, {
          paused: audio.paused,
          ended: audio.ended,
          readyState: audio.readyState,
          networkState: audio.networkState,
          buffered: audio.buffered.length > 0 ? `${audio.buffered.start(0)}-${audio.buffered.end(0)}` : 'none',
          isPlaying: !audio.paused && !audio.ended,
          volume: audio.volume
        })
      }
    }
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsLoading(false)
      setHasError(false)
    }
    
    const handleLoadStart = () => {
      setIsLoading(true)
      setHasError(false)
    }
    
    const handleCanPlay = () => {
      setIsLoading(false)
      audio.volume = volume * GLOBAL_AUDIO_SCALE
    }
    
    const handleError = (e) => {
      console.error('Audio error:', e)
      setHasError(true)
      setIsLoading(false)
      setIsPlaying(false)
    }
    
    const handleEnded = () => {
      setIsPlaying(false)
      // Auto-play next track - pass true to ensure it continues playing
      nextTrack(true)
    }
    
    const handlePlay = () => {
      setIsPlaying(true)
      setHasError(false)
    }
    
    const handlePause = () => {
      // Debug logging for unexpected dirt track pauses
      if (currentTrack?.id === 'my_track_1' && audio.currentTime > 160 && audio.currentTime < 170) {
        console.error(`🚨 Dirt Track PAUSED at ${audio.currentTime.toFixed(1)}s! Stack trace:`)
        console.trace()
      }
      setIsPlaying(false)
    }
    
    const handleWaiting = () => {
      setIsLoading(true)
    }
    
    const handlePlaying = () => {
      setIsLoading(false)
      setIsPlaying(true)
    }
    
    // Enhanced stall detection and recovery
    const handleStalled = () => {
      console.warn('🔄 Audio stalled - immediate recovery attempt...')
      
      if (audio && !audio.ended) {
        const currentTime = audio.currentTime
        const wasPlaying = !audio.paused
        
        console.log(`Stall at ${currentTime.toFixed(2)}s, was playing: ${wasPlaying}`)
        
        // Don't wait - immediate recovery attempt
        try {
          // Check buffer status
          if (audio.buffered.length > 0) {
            const bufferedEnd = audio.buffered.end(audio.buffered.length - 1)
            console.log(`Buffer: ${currentTime.toFixed(2)}s / ${bufferedEnd.toFixed(2)}s`)
          }
          
          // Try to resume without full reload first
          if (wasPlaying) {
            audio.play().catch(() => {
              console.log('🔧 Quick recovery failed, full recovery...')
              // Full recovery as fallback
              audio.load()
              setTimeout(() => {
                if (audio) {
                  audio.currentTime = currentTime
                  audio.play().catch(e => console.warn('❌ Full recovery failed:', e))
                }
              }, 500)
            })
          }
        } catch (error) {
          console.error('Stall recovery error:', error)
        }
      }
    }
    
    // Monitor buffer health for longer tracks
    const handleProgress = () => {
      if (audio.buffered.length > 0) {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1)
        const currentTime = audio.currentTime
        const bufferAhead = bufferedEnd - currentTime
        
        // If buffer is getting low (less than 30 seconds ahead), preload more
        if (bufferAhead < 30 && !audio.paused) {
          console.log('Buffer running low, ensuring continued loading')
        }
      }
    }
    
    // Handle audio suspension (browser energy saving)
    const handleSuspend = () => {
      console.warn('Audio context suspended - will try to resume')
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('loadstart', handleLoadStart)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('error', handleError)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('stalled', handleStalled)
    audio.addEventListener('progress', handleProgress)
    audio.addEventListener('suspend', handleSuspend)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('loadstart', handleLoadStart)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('stalled', handleStalled)
      audio.removeEventListener('progress', handleProgress)
      audio.removeEventListener('suspend', handleSuspend)
    }
  }, [currentTrack, setCurrentTime, setDuration, setIsPlaying, nextTrack, volume, GLOBAL_AUDIO_SCALE])

  // Set volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume * GLOBAL_AUDIO_SCALE
    }
  }, [volume, GLOBAL_AUDIO_SCALE])

  // Reset loading/error states when track changes
  useEffect(() => {
    setIsLoading(true)
    setHasError(false)
  }, [currentTrack])

  // Only render if we have a current track
  if (!currentTrack) return null

  return (
    <>
      <audio
        ref={audioRef}
        src={currentTrack.audioFile}
        preload="auto"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />
      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (isLoading || hasError) && (
        <div style={{ position: 'fixed', top: '10px', right: '10px', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '5px', fontSize: '12px', zIndex: 1000 }}>
          {isLoading && 'Loading...'}
          {hasError && 'Audio Error!'}
        </div>
      )}
    </>
  )
}